import React, { useState, useRef, useEffect } from 'react';
import useOnclickOutside from 'react-cool-onclickoutside';
import useI18n from '@i18n';
import usePortal from '@hooks/usePortal';

import Button from '../Button';

const borderHeight = 2;

interface ICommonDropdown {
    name: string;
    options: {
        key: string;
        value: string;
    }[];
    isChecked: boolean;
    onClick: (value?: { outside: boolean }) => void;
    callback: (value: string) => void;
    visible: boolean;
}

const Dropdown: React.FC<ICommonDropdown> = ({
    name,
    options,
    isChecked,
    onClick,
    callback,
    visible,
}) => {
    const { t } = useI18n('Statistic');
    const ref = useRef();
    const button = useRef(null);

    const setItem = (value: string): void => {
        callback(value);
        onClick();
    };
    const DropdownPortal = usePortal('dropdown');

    const [position, setPosition] = useState({ top: 0, left: 0 });

    const { x: left, y: top, height: buttonHeight } = button.current?.getBoundingClientRect() || {};

    useOnclickOutside(
        (): void => {
            onClick({ outside: true });
        },
        { refs: [ref, button] },
    );

    useEffect((): void => {
        setPosition({
            left,
            top: top + buttonHeight + borderHeight + document.documentElement.scrollTop,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [button, left, top]);

    return (
        <div className="relative">
            <Button ref={button} isChecked={isChecked} onClick={onClick}>
                {t(name)}
            </Button>
            {visible && (
                <DropdownPortal>
                    <div
                        ref={ref}
                        style={{ top: position.top, left: position.left }}
                        className="absolute px-0 bg-white w-[225px] py-[8px] hover:cursor-pointer rounded-3 z-[2] shadow-dark"
                    >
                        {options.map(({ key, value }) => (
                            <button
                                key={key}
                                type="button"
                                className="w-full bg-transparent border-0 py-[4px] px-[12px] text-[16px] text-blueGray-300 leading-[1.5] hover:bg-gray-700 hover:text-black"
                                onClick={() => setItem(value)}
                            >
                                {t(key)}
                            </button>
                        ))}
                    </div>
                </DropdownPortal>
            )}
        </div>
    );
};

export default Dropdown;
